import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { businesses as bizApi, files as filesApi, engagement, loyaltyPrograms } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Phone, MapPin, Star, Ticket, Gift, Loader2, Clock, Globe, Facebook, Instagram, Twitter, Youtube, Music2, PlayCircle, Send, Award, ChevronRight, ShieldCheck, QrCode, Mail } from 'lucide-react';
import AppImage from '../components/AppImage';
import BusinessLogoAvatar from '../components/BusinessLogoAvatar';

function extYouTubeId(url) { if (!url) return null; const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/); return m ? m[1] : null; }
function extVimeoId(url) { if (!url) return null; const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/); return m ? m[1] : null; }
function socialHref(kind, value) {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const h = value.replace(/^@/, '').trim();
  return ({ facebook:`https://facebook.com/${h}`, instagram:`https://instagram.com/${h}`, x:`https://twitter.com/${h}`, tiktok:`https://tiktok.com/@${h}`, youtube:`https://youtube.com/@${h}` })[kind] || value;
}

export default function BusinessDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loyaltyPrograms_, setLoyaltyPrograms] = useState([]);
  const [canReviewState, setCanReviewState] = useState({ canReview: false, hasPermission: false, withinCooldown: false, nextReviewAt: null, permissionSource: null });
  // Wave 6 — group verified badges (pulled from a separate endpoint so
  // we don't touch the existing business fetch).
  const [groupBadges, setGroupBadges] = useState([]);
  const { setUserData, user, refresh } = useAuth();
  const viewTracked = useRef(false);

  const loadCanReview = () => engagement.canReview(id).then(setCanReviewState).catch(() => {});

  useEffect(() => {
    bizApi.get(id).then(setB).catch(() => setB(null)).finally(() => setLoading(false));
    loyaltyPrograms.forBusiness(id).then(setLoyaltyPrograms).catch(() => setLoyaltyPrograms([]));
    engagement.listReviews(id).then(setReviews).catch(() => {});
    engagement.myRating(id).then(r => setMyRating(r?.rating || 0)).catch(() => {});
    // Wave 6 — verified group badges (side call, isolated)
    import('../services/api').then(({ groups: g }) => g.businessBadges(id).then(setGroupBadges).catch(() => setGroupBadges([])));
    loadCanReview();
  }, [id]);

  // Track view once per mount when the listing loads
  useEffect(() => {
    if (b && !viewTracked.current) {
      viewTracked.current = true;
      engagement.track(id, 'view').then(res => {
        if (res.points_awarded) toast({ title: `+${res.points_awarded} pts`, description: 'Thanks for exploring this listing' });
        if (res.user_points != null) refresh();
      }).catch(() => {});
    }
  }, [b, id, refresh]);

  const trackClick = (kind) => engagement.track(id, kind).then(res => {
    if (res.points_awarded) toast({ title: `+${res.points_awarded} pts`, description: 'Nice click!' });
    if (res.user_points != null) refresh();
  }).catch(() => {});

  const redeem = async () => {
    try {
      const res = await bizApi.redeem(id);
      setUserData(res.user);
      toast({ title: 'Coupon redeemed!', description: `+${res.points_added} points added.` });
    } catch (err) {
      toast({ title: 'Cannot redeem', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const submitRating = async (rating) => {
    setMyRating(rating);
    try {
      const res = await engagement.track(id, 'rating', { rating });
      if (res.points_awarded) toast({ title: `+${res.points_awarded} pts`, description: 'Thanks for rating!' });
      else if (res.frozen) toast({ title: 'Review points paused', description: `Frozen until ${new Date(res.reviewsFrozenUntil).toLocaleDateString()}` });
      else if (res.cap_reached) toast({ title: 'Daily cap reached', description: 'Rating saved — no points until tomorrow' });
      await refresh();
      // Refresh business to update avg rating
      const nb = await bizApi.get(id); setB(nb);
    } catch (err) { toast({ title: 'Rating failed', description: err?.response?.data?.detail || 'Try again' }); }
  };

  const submitReview = async () => {
    if (!reviewText.trim()) return;
    setSubmitting(true);
    try {
      const res = await engagement.track(id, 'review', { text: reviewText.trim(), rating: myRating || null });
      const list = await engagement.listReviews(id); setReviews(list);
      setReviewText('');
      if (res.points_awarded) toast({ title: `+${res.points_awarded} pts`, description: 'Thanks for your review!' });
      else if (res.frozen) toast({ title: 'Review posted', description: 'Points paused (frozen). Review still saved.' });
      else if (res.cap_reached) toast({ title: 'Daily cap reached', description: 'Review saved — no points until tomorrow' });
      else toast({ title: 'Review updated' });
      await refresh();
    } catch (err) { toast({ title: 'Review failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>;
  if (!b) return <div>Business not found. <Link to="/directory" className="text-green-400 underline">Back</Link></div>;

  const alreadyRedeemed = (user?.redeemedCoupons || []).includes(b.id);
  const photos = [b.image, ...(b.gallery || [])].filter(Boolean);
  const hero = photos[heroIdx] || b.image;
  const yt = extYouTubeId(b.videoUrl); const vm = extVimeoId(b.videoUrl);
  const social = b.social || {};
  const socials = [
    { kind: 'facebook', icon: Facebook, label: 'Facebook', value: social.facebook },
    { kind: 'instagram', icon: Instagram, label: 'Instagram', value: social.instagram },
    { kind: 'x', icon: Twitter, label: 'X', value: social.x },
    { kind: 'tiktok', icon: Music2, label: 'TikTok', value: social.tiktok },
    { kind: 'youtube', icon: Youtube, label: 'YouTube', value: social.youtube },
  ].filter(s => s.value);

  return (
    <div className="space-y-5 max-w-4xl">
      <Link to="/directory" className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16}/> Back to directory</Link>
      <div className="pbb-card overflow-hidden">
        <div data-testid="business-cover-frame" className="relative">
          <AppImage src={hero} alt={b.name} size="detail" rounded="none" aspect="16/9" />
          <div className="absolute -bottom-8 left-6 z-10">
            <BusinessLogoAvatar business={b} size="lg" />
          </div>
        </div>
        {photos.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto no-scrollbar bg-[#080808] border-t border-[#1f1f1f]">
            {photos.map((p, i) => (
              <button key={i} onClick={() => setHeroIdx(i)} className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${i === heroIdx ? 'border-green-500' : 'border-transparent hover:border-[#2a2a2a]'}`}>
                <img src={filesApi.urlFor(p)} alt="" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        )}
        <div className="p-5 pt-12">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold">{b.name}</h1>
              {groupBadges?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid="business-group-badges">
                  {groupBadges.map(g => (
                    <Link key={g.id} to={`/groups/${g.slug}`} data-testid={`business-group-badge-${g.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-green-500/40 bg-green-500/10 text-green-300 hover:bg-green-500/20">
                      ✓ {g.name}
                    </Link>
                  ))}
                </div>
              )}
              <div className="text-sm text-gray-400 mt-1 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1"><Star size={14} className="text-green-400"/> {b.rating?.toFixed?.(1) || b.rating || '—'} ({b.reviews || 0})</span>
                <span className="inline-flex items-center gap-1"><MapPin size={14}/> {b.address ? `${b.address}, ${b.city}` : b.city}</span>
                {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-green-400"><Phone size={14}/> {b.phone}</a>}
                {b.hours && <span className="inline-flex items-center gap-1"><Clock size={14}/> {b.hours}</span>}
              </div>
            </div>
            <span className="pbb-chip active">{b.category}</span>
          </div>

          {(b.website || socials.length > 0) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {b.website && (
                <a onClick={() => trackClick('website_click')} href={/^https?:\/\//i.test(b.website) ? b.website : `https://${b.website}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600/10 border border-green-600/30 text-green-400 hover:bg-green-600/20 text-sm">
                  <Globe size={14}/> Visit website
                </a>
              )}
              {socials.map(s => (
                <a key={s.kind} onClick={() => trackClick('social_click')} href={socialHref(s.kind, s.value)} target="_blank" rel="noopener noreferrer" title={s.label}
                  className="w-9 h-9 rounded-full border border-[#2a2a2a] hover:border-green-500 hover:text-green-400 text-gray-300 flex items-center justify-center transition-colors">
                  <s.icon size={16}/>
                </a>
              ))}
            </div>
          )}

          {b.description && <div className="mt-5 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{b.description}</div>}

          {(yt || vm || b.video) && (
            <div className="mt-5 rounded-lg overflow-hidden bg-black border border-[#1f1f1f]">
              {yt ? (
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe src={`https://www.youtube.com/embed/${yt}`} title="YouTube" className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>
                </div>
              ) : vm ? (
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe src={`https://player.vimeo.com/video/${vm}`} title="Vimeo" className="absolute inset-0 w-full h-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen/>
                </div>
              ) : (
                <video src={filesApi.urlFor(b.video)} controls className="w-full max-h-[420px] bg-black" playsInline/>
              )}
              <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2"><PlayCircle size={12} className="text-green-400"/> Watch a quick tour</div>
            </div>
          )}

          {b.coupon && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg bg-green-600/10 border border-green-600/30 p-4">
                <div className="flex items-center gap-2 text-green-400 font-semibold"><Ticket size={16}/> Coupon</div>
                <div className="mt-1">{b.coupon}</div>
                <button onClick={redeem} disabled={alreadyRedeemed} className="mt-3 pbb-btn-green disabled:opacity-60 disabled:cursor-not-allowed">
                  {alreadyRedeemed ? 'Already Redeemed' : `Redeem & Earn +${b.points} pts`}
                </button>
              </div>
              <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-4">
                <div className="flex items-center gap-2 text-green-400 font-semibold"><Gift size={16}/> Loyalty {loyaltyPrograms_.length > 0 ? 'programs' : 'perks'}</div>
                {loyaltyPrograms_.length === 0 ? (
                  <ul className="text-sm text-gray-300 mt-2 space-y-1 list-disc pl-5">
                    <li>Earn {b.points} points per visit</li>
                    <li>Unlock exclusive PBB-member pricing</li>
                    <li>Contribute to community & youth funds</li>
                  </ul>
                ) : (
                  <div data-testid="business-loyalty-programs" className="mt-2 space-y-2">
                    {loyaltyPrograms_.map((lp) => (
                      <div key={lp.id} data-testid={`biz-loyalty-${lp.id}`} className="rounded-lg border border-[#153b23] bg-black/30 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-white">{lp.name}</div>
                            <div className="text-[11px] text-gray-400 mt-0.5">{lp.reward}</div>
                          </div>
                          <Award size={14} className="text-green-400 shrink-0" />
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-400">
                          {lp.visits_required ? <span>🎯 {lp.visits_required} visits</span> : null}
                          {lp.points_per_visit ? <span>💚 +{lp.points_per_visit} pts / visit</span> : null}
                          {lp.points_to_redeem ? <span>🎁 Redeem at {lp.points_to_redeem} pts</span> : null}
                        </div>
                      </div>
                    ))}
                    <Link to="/loyalty" data-testid="biz-loyalty-see-all" className="inline-flex items-center gap-1 text-[11px] text-green-400 hover:text-green-300 mt-1">
                      Browse all loyalty programs <ChevronRight size={12} />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ratings + Reviews */}
      <div className="pbb-card p-5 space-y-4" data-testid="reviews-block">
        {/* Verification gate */}
        {!canReviewState.canReview && (
          <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-4 space-y-2" data-testid="review-gate">
            <div className="inline-flex items-center gap-2 text-sm">
              <ShieldCheck size={16} className="text-green-400"/>
              <b>Reviews are verified only</b>
            </div>
            {canReviewState.withinCooldown ? (
              <div className="text-xs text-gray-400">You already reviewed this business. Come back after <b className="text-white">{canReviewState.nextReviewAt ? new Date(canReviewState.nextReviewAt).toLocaleDateString() : 'soon'}</b>.</div>
            ) : (
              <>
                <div className="text-xs text-gray-400">To keep reviews trustworthy, we only accept them from customers who:</div>
                <ul className="text-xs text-gray-300 list-disc pl-5 space-y-1">
                  <li className="inline-flex items-center gap-1"><QrCode size={12} className="text-green-400"/> Scan the on-site QR code at the business (within 500 m).</li>
                  <li className="inline-flex items-center gap-1"><Mail size={12} className="text-green-400"/> Used a single-use invite link sent by the business owner.</li>
                </ul>
              </>
            )}
          </div>
        )}
        {canReviewState.canReview && (
          <div className="rounded-lg bg-green-600/10 border border-green-600/40 p-3 text-xs text-green-300 inline-flex items-center gap-2" data-testid="review-verified">
            <ShieldCheck size={14}/> You're verified for this business ({canReviewState.permissionSource === 'qr' ? 'in-store QR scan' : 'owner invite'}). Rate + review below.
          </div>
        )}

        <div>
          <div className="font-semibold">Rate this business</div>
          <div className="flex items-center gap-1 mt-2">
            {[1,2,3,4,5].map(n => (
              <button key={n} onClick={() => submitRating(n)} disabled={!canReviewState.canReview} className="p-1 disabled:opacity-40 disabled:cursor-not-allowed">
                <Star size={22} className={n <= myRating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'}/>
              </button>
            ))}
            {myRating > 0 && <span className="text-xs text-gray-400 ml-2">Your rating: {myRating}/5</span>}
          </div>
        </div>

        <div className="pt-3 border-t border-[#1f1f1f]">
          <div className="font-semibold mb-2">Write a review</div>
          <textarea rows={3} value={reviewText} onChange={e => setReviewText(e.target.value)} disabled={!canReviewState.canReview} className="pbb-input disabled:opacity-50" placeholder={canReviewState.canReview ? "Share your experience..." : "Verify first to leave a review."}/>
          <button onClick={async () => { await submitReview(); loadCanReview(); }} disabled={submitting || !reviewText.trim() || !canReviewState.canReview} data-testid="submit-review-btn" className="mt-3 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-sm text-white inline-flex items-center gap-2 disabled:opacity-70">
            {submitting ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Post review
          </button>
        </div>

        <div className="pt-3 border-t border-[#1f1f1f]">
          <div className="font-semibold mb-3">Reviews ({reviews.length})</div>
          {reviews.length === 0 ? (
            <div className="text-sm text-gray-500">No reviews yet. Be the first!</div>
          ) : (
            <ul className="space-y-3">
              {reviews.map(r => (
                <li key={r.id} className="flex items-start gap-3">
                  <img src={filesApi.urlFor(r.userAvatar) || 'https://i.pravatar.cc/60'} alt="" className="w-9 h-9 rounded-full object-cover"/>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">{r.userName}</div>
                      {r.rating && <div className="text-xs text-yellow-400">{'★'.repeat(r.rating)}<span className="text-gray-600">{'★'.repeat(5-r.rating)}</span></div>}
                      <div className="text-xs text-gray-500 ml-auto">{new Date(r.createdAt).toLocaleDateString()}</div>
                    </div>
                    <div className="text-sm text-gray-200 mt-1 whitespace-pre-wrap">{r.text}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
